/*
 * Wrapper for all kinds of charts
 * handles headings etc
 *
 */

import { getNode, stripHtml } from './utils';
import { getTranslationFunction } from './language';
import { getColors } from './colors';

const t = getTranslationFunction('global');

const templateString = `
  <div class="tsg-chart">
    <div class="tsg-chart__top">
      <div class="tsg-chart__heading tsg-font-heading">{heading}</div>
      <div class="tsg-chart__text tsg-font-text">{text}</div>
    </div>
    <div class="tsg-chart__content"></div>
    <div class="tsg-chart__bottom">
      <div class="tsg-chart__bottom-open tsg-font-fineprint">{bOpen}</div>
      <div class="tsg-chart__bottom-close tsg-font-fineprint">{bClose}</div>
      <div class="tsg-chart__fineprint tsg-font-fineprint">
        {fineprint}
      </div>
      <div class="tsg-chart__attribution tsg-font-attribution">{attribution}</div>
    </div>
  </div>
`;

const us = '<a href="https://www.investigate-europe.eu/" target="_blank">Investigate Europe</a>, <a href="https://www.reportersunited.gr/en/" target="_blank">Reporters United</a>, <a href="https://interaktiv.tagesspiegel.de/" target="_blank">Tagesspiegel Innovation Lab</a>';

function template(d) {
  return templateString
    .replace('{heading}', stripHtml(d.heading || ''))
    .replace('{text}', stripHtml(d.text || ''))
    .replace('{fineprint}', stripHtml(d.fineprint || ''))
    .replace('{attribution}', `${(d.attribution ? `${t('Data')}: ${d.attribution} / ` : '') + t('Graphic')}: ${us}`)
    .replace('{bOpen}', d.fineprint || d.attribution ? t('Show source and methods') : '')
    .replace('{bClose}', d.fineprint || d.attribution ? t('Hide source and methods') : '')
    .replace(/>\s+</g, '><')
    .trim();
}


class Chart {
  constructor(containerOrSelector, d) {
    this.container = getNode(containerOrSelector);
    this.container.innerHTML = template(d);

    this.elems = {
      main: this.container.querySelector('.tsg-chart'),
      content: this.container.querySelector('.tsg-chart__content'),
      top: this.container.querySelector('.tsg-chart__top'),
      bottom: this.container.querySelector('.tsg-chart__bottom'),

      heading: this.container.querySelector('.tsg-chart__heading'),
      text: this.container.querySelector('.tsg-chart__text'),
      fineprint: this.container.querySelector('.tsg-chart__fineprint'),
      attribution: this.container.querySelector('.tsg-chart__attribution'),
      bottomOpen: this.container.querySelector('.tsg-chart__bottom-open'),
      bottomClose: this.container.querySelector('.tsg-chart__bottom-close'),
    };

    if (d.fixedHeight) {
      this.setupHeightChecks();
      this.elems.main.classList.add('tsg-chart--fh');
    }
    if (d.editMode) this.setupEditMode(d.editCallback);
    if (d.hideattribution) this.setupHideBottom();


    if (d.headingMaxWidth) {
      this.elems.top.style.maxWidth = `${d.headingMaxWidth}px`;
      this.elems.top.style.marginLeft = 'auto';
      this.elems.top.style.marginRight = 'auto';
    }

    const colors = getColors();
    if (colors.setBackground) this.elems.main.style.backgroundColor = colors.bg;
  }

  setupHeightChecks() {
    // TODO: still need to figure out whether communication with the chart is
    // necessary (probably not)
    // TODO: check if this works :)

    window.addEventListener('resize', () => {
      const hTop = this.elems.top.getBoundingClientRect().height;
      const hBottom = this.elems.content.getBoundingClientRect().height;
      const h = window.innerHeight;
      this.elems.content.style.height = h - hTop - hBottom;
    });
  }

  setupEditMode(cbk) {
    this.elems.main.classList.add('tsg-chart--edit');

    this.elems.heading.contentEditable = true;
    this.elems.text.contentEditable = true;
    this.elems.fineprint.contentEditable = true;

    const preventEnter = (e) => { if (e.which === 13) e.preventDefault(); };
    const stopPropagation = (e) => { e.stopPropagation(); };
    this.elems.heading.addEventListener('keydown', preventEnter);
    this.elems.text.addEventListener('keydown', preventEnter);
    this.elems.fineprint.addEventListener('keydown', preventEnter);
    this.elems.fineprint.addEventListener('click', stopPropagation);

    /*
    function clean(x) {
      return x
        .replace(/<br>/g, ' ')
        .replace(/<div>/g, ' ')
        .replace(/<\/div>/g, ' ')
        .replace(/<p>/g, ' ')
        .replace(/<\/p>/g, ' ')
        .replace(/ {2}/g, ' ')
        .trim();
    }
    */

    if (cbk) {
      const sendEdit = () => {
        cbk({
          /*
          heading: clean(this.elems.heading.innerHTML),
          text: clean(this.elems.text.innerHTML),
          fineprint: clean(this.elems.fineprint.innerHTML),
          */
          heading: this.elems.heading.textContent,
          text: this.elems.text.textContent,
          fineprint: this.elems.fineprint.textContent,
        });
      };
      this.elems.heading.addEventListener('keyup', sendEdit);
      this.elems.text.addEventListener('keyup', sendEdit);
      this.elems.fineprint.addEventListener('keyup', sendEdit);
    }
  }

  setupHideBottom() {
    this.elems.main.classList.add('tsg-chart--hide-bottom');
    this.elems.main.classList.add('tsg-chart--bottom-hidden');
    this.elems.bottomClose.addEventListener('click', () => {
      this.elems.main.classList.add('tsg-chart--bottom-hidden');
    });
    this.elems.bottomOpen.addEventListener('click', () => {
      this.elems.main.classList.remove('tsg-chart--bottom-hidden');
    });
  }
}


export default Chart;
